// ============================================================================
// hooks/liveStreamManagment/useAudioMetering.ts
//
// v4.2 — Intégration audioMeter factory :
//   - Une instance createMeter('ppm') par source, créée à la connexion
//     et détruite à la déconnexion.
//   - getFloatTimeDomainData au lieu de getByteFrequencyData.
//   - Les niveaux sont en dB (-60 à +6) et convertis en pourcentage 0-100
//     pour rester compatibles avec les composants existants (VU-mètres, deck).
//   - Conversion dB → % : (db - DB_FLOOR) / (DB_CEIL - DB_FLOOR) * 100
//
// v5.0 — Les niveaux sortent de React. La boucle rAF publie dans
//   `audioLevelStore` au lieu d'appeler setState : plus aucun rendu React n'est
//   déclenché au rythme des VU-mètres. Les mètres s'y abonnent et écrivent le
//   DOM directement (cf. hooks/liveStreamManagment/useAudioLevel.ts).
//   `stereoLevels` a été supprimé : la prop était transmise jusqu'à
//   LiveStreamLayoutV2 mais n'y était jamais lue.
//
// v5.1 — Changement de source audio par l'engrenage (2026-10-15). Deux défauts
//   faisaient ignorer le choix de l'opérateur :
//   • changeAudioDevice débranchait le slot puis attendait getUserMedia ; pendant
//     ce temps le rendu provoqué par le nouveau `selectedAudioDeviceIds` relançait
//     l'auto-connexion, qui branchait le slot une seconde fois. L'AudioSourceRegistry
//     garde la PREMIÈRE entrée et se contente d'incrémenter son refCount : une
//     caméra invitée, un HLS ou un écran gardaient leur propre son (branché
//     aussitôt) à la place du micro choisi, et une webcam restait à refCount 2 —
//     le changement suivant ne la libérait plus, et l'ancien micro restait en
//     place pour toute la session (« déjà connecté, skip »). Un slot en cours de
//     changement est désormais réservé (`pendingSourcesRef`), les changements d'un
//     même slot s'enchaînent au lieu de se croiser, et l'ancienne entrée est
//     libérée sans tenir compte du refCount.
//   • connectAudioForSlot ne connaissait pas les redirections `hls-audio-*`,
//     `remote-audio-*`, `screen-audio-*` : tout rebranchement (configuration
//     rechargée, invité reconnecté) les perdait. Les deux chemins passent
//     maintenant par `attachSlotAudio`, qui applique la même règle.
//
// v5.2 — La relance « un flux devient disponible » suit la redirection `hls-audio-*` du
//   slot (2026-10-16) : une préférence mémorisée avant l'acquisition de la playlist
//   n'attendait que le flux propre du slot et ne se rebranchait jamais seule.
//
// v5.3 — Un slot redirigé vers le son d'une AUTRE playlist (`hls-audio-X`) tient un emprunt
//   sur cette playlist tant que la redirection dure (2026-10-16). Avant, rien ne la
//   gardait en lecture : dès qu'elle quittait l'antenne et que plus rien ne l'empruntait,
//   HlsAcquirer détruisait le lecteur et la mettait en pause — la piste restait « live »
//   mais muette, et toutes les entrées qui la partagent tombaient à zéro (mesuré par
//   [ASR-PROBE]). C'est aussi l'acquisition à la demande : une playlist non acquise est
//   acquise par cet emprunt au lieu de laisser le choix muet.
//
// v4.1 — resolveStream pour 'device' consulte le SourceRegistry vidéo.
// v4   — Correctifs HLS, mute, changeAudioDevice.
// ============================================================================

import { useEffect, useRef, useCallback } from 'react';
import { VideoSource, SourceAudioConfig } from '../../contexts/MediaStreamContext';
import type { BorrowHandle } from '../../dataTypes/liveStreamManagment/sourceRegistry/types';
import { useAudioMixer } from '../../contexts/liveStreamManagment/AudioMixerContext';
import { useSourceRegistry } from '../../contexts/liveStreamManagment/SourceRegistryContext';
import { createMeter, MeterInstance } from '../../dataTypes/liveStreamManagment/audioPipeline/audioMeter';
import { audioLevelStore, SourceLevels } from '../../dataTypes/liveStreamManagment/audioPipeline/audioLevelStore';
import { AUDIO_EFFECTS } from '../../components/block/liveStreamManagment/audioEffects/registry';
import { getVuMeterMode } from '../../components/block/liveStreamManagment/vuMeterZones';
import { LUFS_FLOOR, MOMENTARY_WINDOW_S, lufsFromMeanSquares, meanSquareTail } from '../../dataTypes/liveStreamManagment/audioPipeline/loudness';

/** Mode LUFS : intervalle de recalcul de la loudness d'une source (20 par seconde). */
const LUFS_REFRESH_MS = 50;

// ── Constantes ────────────────────────────────────────────────────────────────

const DB_FLOOR = -60;
const DB_CEIL  =   6;

/** Convertit une valeur dB en pourcentage 0–100 pour les barres UI */
function dbToPct(db: number): number {
  return Math.max(0, Math.min(100, ((db - DB_FLOOR) / (DB_CEIL - DB_FLOOR)) * 100));
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UseAudioMeteringOptions {
  deckSlots:              (VideoSource | null)[];
  // `activeSourceId` a disparu en v5.0 : il ne servait qu'à alimenter
  // `stereoLevels`, dont personne ne lisait la valeur.
  selectedAudioDeviceIds: Map<string, string>;
  remoteConnections:      Map<string, any>;
  screenShareStreams:     Map<string, MediaStream>;
  getSourceAudioConfig?:  (sourceId: string) => SourceAudioConfig;
}

/**
 * v5.0 — les niveaux ne transitent plus par cette valeur de retour : ils sont
 * publiés dans `audioLevelStore` et lus par les VU-mètres via
 * `useAudioLevelEffect`. Aucun rendu React n'est déclenché par un niveau.
 */
export interface UseAudioMeteringReturn {
  connectAudioForSlot:    (source: VideoSource) => Promise<void>;
  disconnectAudioForSlot: (sourceId: string, keepSourceHold?: boolean) => void;
  changeAudioDevice:      (sourceId: string, newDeviceId: string) => Promise<void>;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAudioMetering(options: UseAudioMeteringOptions): UseAudioMeteringReturn {
  const {
    deckSlots,
    selectedAudioDeviceIds,
    remoteConnections,
    screenShareStreams,
    getSourceAudioConfig,
  } = options;

  const {
    audioSourceRegistry,
    connectAudioForSource,
    disconnectAudioForSource,
    getSourceAnalysers,
    getSourceLoudnessAnalysers,
    hasAudioSource,
  } = useAudioMixer();

  const { registry, sources } = useSourceRegistry();

  // ── Refs ────────────────────────────────────────────────────────────────────

  const connectedSourcesRef = useRef<Set<string>>(new Set());
  const meterRafRef         = useRef<number | null>(null);

  // Slots dont l'audio est en train d'être (re)branché : l'auto-connexion doit
  // les laisser tranquilles, sinon elle ouvre une seconde entrée concurrente.
  const pendingSourcesRef = useRef<Set<string>>(new Set());

  // Emprunts tenus sur la source dont dépend l'audio d'un slot (clé = slot). Un seul par
  // slot : la source redirigée (hls-audio-X). Rendu quand la redirection change ou que le
  // slot quitte le deck.
  const sourceHoldsRef = useRef<Map<string, BorrowHandle>>(new Map());
  // Dernier changement de source audio demandé par slot : les suivants
  // attendent qu'il ait fini plutôt que de se croiser dans le registry.
  const changeQueueRef    = useRef<Map<string, Promise<void>>>(new Map());

  // Lu au moment où un changement s'exécute (il peut attendre le précédent) :
  // le slot a pu changer de source, ou quitter le deck, entre-temps.
  const deckSlotsRef = useRef(deckSlots);
  useEffect(() => { deckSlotsRef.current = deckSlots; }, [deckSlots]);

  // Une instance de mètre PPM par source
  const meterInstancesRef = useRef<Map<string, MeterInstance>>(new Map());

  // Mode LUFS : tampons longs du signal pondéré K, et dernière valeur par source
  // (recalculée toutes les LUFS_REFRESH_MS, la norme demande ≥ 10 par seconde).
  const loudBuffersRef = useRef<Map<string, { dataL: Float32Array; dataR: Float32Array }>>(new Map());
  const loudLastRef    = useRef<Map<string, { at: number; lufs: number }>>(new Map());

  // Buffers Float32 réutilisés par source (évite les allocations dans la boucle)
  const floatBuffersRef = useRef<Map<string, { dataL: Float32Array; dataR: Float32Array }>>(new Map());

  const getAudioCfgRef = useRef(getSourceAudioConfig);
  useEffect(() => {
    getAudioCfgRef.current = getSourceAudioConfig;
  }, [getSourceAudioConfig]);

  // ── Réapplication des niveaux enregistrés à une entrée de registry ────────
  //
  // AudioSourceRegistry recrée une entrée neuve (gainNode/gainLeft/gainRight à 1) à
  // chaque (re)connexion d'une source. L'effet de synchronisation plus bas ne se
  // redéclenche, lui, que lorsque l'identité de `getSourceAudioConfig` change, c'est-à-dire
  // quand l'opérateur touche un réglage : après une reconnexion (device rebranché, remote
  // qui revient, changement de périphérique audio), les niveaux mémorisés étaient donc
  // silencieusement perdus et la source repartait à 0 dB. On les réapplique à chaud.
  const applyStoredLevels = useCallback((sourceId: string) => {
    const cfg = getAudioCfgRef.current?.(sourceId);
    if (!cfg) return;
    const regId = `audio-${sourceId}`;
    if (!audioSourceRegistry.has(regId)) return;
    audioSourceRegistry.setMuted(regId, cfg.muted ?? false);
    // Indépendant du mute — un lowcut/reverb réglé doit survivre à une coupure du son,
    // pour retrouver son état à la reprise sans que l'opérateur n'ait à revalider.
    for (const effect of AUDIO_EFFECTS) {
      const enabled = cfg.audioEffects?.[effect.id] ?? false;
      const config  = cfg.audioEffectConfigs?.[effect.id] ?? effect.defaultConfig;
      effect.apply(audioSourceRegistry, regId, enabled, config);
    }
    if (cfg.muted) return;
    if (cfg.volume      !== undefined) audioSourceRegistry.setGain(regId,      cfg.volume);
    if (cfg.volumeLeft  !== undefined) audioSourceRegistry.setGainLeft(regId,  cfg.volumeLeft);
    if (cfg.volumeRight !== undefined) audioSourceRegistry.setGainRight(regId, cfg.volumeRight);
  }, [audioSourceRegistry]);

  // ── Résolution du stream audio d'une source ────────────────────────────────

const resolveStream = useCallback((source: VideoSource): MediaStream | null => {
  if (source.type === 'device') {
    const stream = registry.getStream(source.id) ?? null;
    console.log('[METER] resolveStream device', source.id.slice(0,8), 'stream=', stream?.id ?? 'null');
    return stream;
  }
  if (source.type === 'remote') {
    const deviceId = source.id.replace('remote-', '');
    const conn = remoteConnections.get(deviceId);
    console.log('[METER] resolveStream remote', source.id,
      'deviceId=', deviceId,
      'conn=', !!conn,
      'conn.stream=', conn?.stream?.id ?? 'null',
      'audioTracks=', conn?.stream?.getAudioTracks().length ?? 0,
      'muted=', conn?.stream?.getAudioTracks()[0]?.muted ?? 'n/a');
    return conn?.stream ?? source.stream ?? null;
  }
  if (source.type === 'screen') {
    const stream = screenShareStreams.get(source.id) ?? source.stream ?? null;
    console.log('[METER] resolveStream screen', source.id, 'stream=', stream?.id ?? 'null');
    return stream;
  }
  if (source.type === 'url') {
    const stream = registry.getStream(source.id) ?? source.stream ?? null;
    console.log('[METER] resolveStream url', source.id.slice(0,8), 'stream=', stream?.id ?? 'null');
    return stream;
  }
  return null;
}, [remoteConnections, screenShareStreams, registry]);


  // ── Branchement de l'audio d'un slot, redirections comprises ──────────────
  //
  // Seul endroit qui décide QUEL son alimente l'entrée `audio-<slot>` :
  //   • `hls-audio-<slot>`    → le son d'une source HLS du deck ;
  //   • `remote-audio-<id>`   → le son d'une caméra invitée ;
  //   • `screen-audio-<slot>` → le son d'un partage d'écran ;
  //   • autre valeur          → un micro du système, ouvert par getUserMedia ;
  //   • vide (« Audio par défaut ») → le son propre de la source.
  // Rend false quand il n'y a encore rien à brancher (flux pas encore là) : le
  // slot n'est alors pas compté comme branché, et l'auto-connexion le reprendra.
  // Un micro compte toujours comme branché, même refusé : sans quoi chaque rendu
  // redemanderait l'autorisation au navigateur.
  const releaseSourceHold = useCallback((slotId: string) => {
    const hold = sourceHoldsRef.current.get(slotId);
    if (!hold) return;
    sourceHoldsRef.current.delete(slotId);
    try { hold.release(); } catch {}
  }, []);

  // Garde `targetId` en lecture pour le slot. On prend le nouvel emprunt AVANT de rendre
  // l'ancien : une playlist qui change de demandeur ne tombe jamais à zéro emprunt.
  const holdSource = useCallback(async (slotId: string, targetId: string): Promise<MediaStream | null> => {
    const current = sourceHoldsRef.current.get(slotId);
    if (current?.sourceId === targetId) return registry.getStream(targetId) ?? current.stream;
    try {
      const handle = await registry.borrow(targetId);
      releaseSourceHold(slotId);
      sourceHoldsRef.current.set(slotId, handle);
      return registry.getStream(targetId) ?? handle.stream;
    } catch (err) {
      console.warn(`[useAudioMetering] impossible de tenir la source ${targetId} pour ${slotId} :`, err);
      return null;
    }
  }, [registry, releaseSourceHold]);

  const attachSlotAudio = useCallback(async (source: VideoSource, audioDeviceId: string | undefined): Promise<boolean> => {
    let stream: MediaStream | null | undefined;
    let type: 'device' | 'remote' | 'screen' | 'url';

    // Redirection vers une AUTRE playlist : on la tient en lecture. Un slot qui lit sa
    // propre playlist (redirection vers lui-même) garde le comportement d'origine.
    const redirectedHls = audioDeviceId?.startsWith('hls-audio-')
      ? audioDeviceId.slice('hls-audio-'.length) : null;
    if (!redirectedHls || redirectedHls === source.id) releaseSourceHold(source.id);

    if (redirectedHls) {
      stream = redirectedHls === source.id
        ? registry.getStream(redirectedHls)
        : await holdSource(source.id, redirectedHls);
      type   = 'url';
    } else if (audioDeviceId?.startsWith('remote-audio-')) {
      stream = remoteConnections.get(audioDeviceId.slice('remote-audio-'.length))?.stream;
      type   = 'remote';
    } else if (audioDeviceId?.startsWith('screen-audio-')) {
      stream = screenShareStreams.get(audioDeviceId.slice('screen-audio-'.length));
      type   = 'screen';
    } else if (audioDeviceId) {
      await connectAudioForSource(source.id, 'device', null, audioDeviceId);
      return true;
    } else {
      stream = resolveStream(source);
      type   = source.type as typeof type;
    }

    if (!stream || stream.getAudioTracks().length === 0) {
      console.log(`[useAudioMetering] attachSlotAudio(${source.id}) — son ${audioDeviceId || 'propre'} pas encore disponible, report`);
      return false;
    }
    await connectAudioForSource(source.id, type, stream);
    return true;
  }, [connectAudioForSource, registry, remoteConnections, screenShareStreams, resolveStream, holdSource, releaseSourceHold]);

  // Branche puis équipe le slot (niveaux mémorisés, mètre). Le slot a pu quitter
  // le deck pendant l'attente de getUserMedia : on ne garde alors rien.
  const attachAndMeterSlot = useCallback(async (source: VideoSource, audioDeviceId: string | undefined) => {
    const attached = await attachSlotAudio(source, audioDeviceId);
    if (!attached) return;
    if (!deckSlotsRef.current.some(s => s?.id === source.id)) {
      disconnectAudioForSource(source.id);
      releaseSourceHold(source.id);
      return;
    }
    applyStoredLevels(source.id);
    meterInstancesRef.current.set(source.id, createMeter('ppm'));
    connectedSourcesRef.current.add(source.id);
    console.log(`[useAudioMetering] ${source.id} — audio branché (${audioDeviceId || 'son propre'}), mètre PPM créé`);
  }, [attachSlotAudio, disconnectAudioForSource, applyStoredLevels, releaseSourceHold]);

  // File par slot : branchement automatique et changement de source audio
  // s'exécutent l'un après l'autre, jamais en même temps. La réservation est
  // posée DÈS l'appel, avant tout await : le rendu qui suit un changement relance
  // l'auto-connexion, qui doit trouver le slot déjà pris.
  const enqueueSlotAudio = useCallback((sourceId: string, op: () => Promise<void>): Promise<void> => {
    pendingSourcesRef.current.add(sourceId);
    const previous = changeQueueRef.current.get(sourceId) ?? Promise.resolve();
    const run = previous.catch(() => {}).then(op);
    changeQueueRef.current.set(sourceId, run);
    const settle = () => {
      if (changeQueueRef.current.get(sourceId) !== run) return; // une opération plus récente attend
      changeQueueRef.current.delete(sourceId);
      pendingSourcesRef.current.delete(sourceId);
    };
    run.then(settle, settle);
    return run;
  }, []);

  // ── Connexion audio d'un slot ─────────────────────────────────────────────

  const connectAudioForSlot = useCallback(async (source: VideoSource) => {

    console.log('[METER] connectAudioForSlot ENTRÉE', source.id, 'type=', source.type,
      'alreadyConnected=', connectedSourcesRef.current.has(source.id),
      'pending=', pendingSourcesRef.current.has(source.id));

    if (connectedSourcesRef.current.has(source.id)) return;
    // Un branchement ou un changement de source audio est déjà en cours sur ce slot.
    if (pendingSourcesRef.current.has(source.id)) return;

    // Une source audio seul précise porte son entrée dans `deviceId` ; « Vide (audio
    // seul) » n'en a pas et suit, comme une caméra, le choix fait dans ses réglages.
    const audioDeviceId = selectedAudioDeviceIds.get(source.id)
      || (source.type === 'audio' ? source.deviceId : undefined) || undefined;
    await enqueueSlotAudio(source.id, async () => {
      if (connectedSourcesRef.current.has(source.id)) return;
      await attachAndMeterSlot(source, audioDeviceId);
    });
  }, [attachAndMeterSlot, enqueueSlotAudio, selectedAudioDeviceIds]);

  // ── Déconnexion audio d'un slot ───────────────────────────────────────────

  const disconnectAudioForSlot = useCallback((sourceId: string, keepSourceHold = false) => {
    if (!keepSourceHold) releaseSourceHold(sourceId);
    if (!connectedSourcesRef.current.has(sourceId)) return;
    disconnectAudioForSource(sourceId);
    meterInstancesRef.current.delete(sourceId);
    floatBuffersRef.current.delete(sourceId);
    connectedSourcesRef.current.delete(sourceId);
    console.log(`[useAudioMetering] disconnectAudioForSlot(${sourceId}) — mètre détruit`);
  }, [disconnectAudioForSource, releaseSourceHold]);

  // ── Changement de device audio ────────────────────────────────────────────

  const changeAudioDevice = useCallback((sourceId: string, newDeviceId: string): Promise<void> => {
    console.log(`[useAudioMetering] changeAudioDevice(${sourceId}) → ${newDeviceId}`);
    return enqueueSlotAudio(sourceId, async () => {
      // Le nouvel emprunt de source est pris AVANT que l'ancien soit rendu (cf. holdSource).
      disconnectAudioForSlot(sourceId, true);
      // Entrée encore présente = empruntée plusieurs fois (reste de l'ancien
      // double branchement) ou plus suivie par le contexte : la libérer quand
      // même, sinon connectAudioForSource la réutiliserait telle quelle.
      const regId = `audio-${sourceId}`;
      if (audioSourceRegistry.has(regId)) {
        console.warn(`[useAudioMetering] changeAudioDevice(${sourceId}) — ancienne entrée encore empruntée, libération forcée`);
        audioSourceRegistry.forceRelease(regId);
      }

      const source = deckSlotsRef.current.find(s => s?.id === sourceId);
      if (!source) return;
      await attachAndMeterSlot(source, newDeviceId || undefined);
    });
  }, [enqueueSlotAudio, disconnectAudioForSlot, attachAndMeterSlot, audioSourceRegistry]);

  // ── Auto-connexion des sources du deck ────────────────────────────────────

  useEffect(() => {
    const currentSlotIds = new Set<string>();
    deckSlots.forEach(slot => {
      if (!slot) return;
      currentSlotIds.add(slot.id);
      if (!connectedSourcesRef.current.has(slot.id)) {
        connectAudioForSlot(slot).catch(err => {
          console.warn(`[useAudioMetering] auto-connect failed for ${slot.id}:`, err);
        });
      }
    });
    connectedSourcesRef.current.forEach(id => {
      if (!currentSlotIds.has(id)) disconnectAudioForSlot(id);
    });
  }, [deckSlots, connectAudioForSlot, disconnectAudioForSlot]);

  // ── Retry quand un stream devient disponible dans le registry ─────────────

  useEffect(() => {
    deckSlots.forEach(slot => {
      if (!slot) return;
      if (connectedSourcesRef.current.has(slot.id)) return;
      // Audio seul : son entrée (smartphone, écran, playlist) a pu arriver après lui.
      // attachSlotAudio ne branche que ce qui est déjà là et reporte le reste.
      if (slot.type === 'audio') {
        if (selectedAudioDeviceIds.get(slot.id) || slot.deviceId) connectAudioForSlot(slot).catch(() => {});
        return;
      }
      if (slot.type !== 'url' && slot.type !== 'device') return;
      // Un slot redirigé vers le son d'une playlist (`hls-audio-<slot>`) attend CE flux-là,
      // pas le sien : sinon une préférence mémorisée, demandée avant que la playlist soit
      // acquise, restait reportée jusqu'à ce que l'opérateur rouvre les réglages.
      const wanted = selectedAudioDeviceIds.get(slot.id);
      const stream = wanted?.startsWith('hls-audio-')
        ? registry.getStream(wanted.slice('hls-audio-'.length))
        : registry.getStream(slot.id);
      if (stream && stream.getAudioTracks().length > 0) {
        connectAudioForSlot(slot).catch(() => {});
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources]);

  // ── Synchronisation mute / volume → AudioSourceRegistry ──────────────────

  useEffect(() => {
    if (!getSourceAudioConfig) return;
    connectedSourcesRef.current.forEach(applyStoredLevels);
  }, [getSourceAudioConfig, audioSourceRegistry, applyStoredLevels]);

  // ── Re-connexion remote ───────────────────────────────────────────────────


  useEffect(() => {
    deckSlots.forEach(slot => {
      if (!slot || slot.type !== 'remote') return;
      if (!connectedSourcesRef.current.has(slot.id)) return;
      const deviceId = slot.id.replace('remote-', '');
      const conn     = remoteConnections.get(deviceId);
      if (conn?.stream && !hasAudioSource(slot.id)) {
        disconnectAudioForSlot(slot.id);
        connectAudioForSlot(slot).catch(() => {});
      }
    });
  }, [remoteConnections, deckSlots]);




  // ── Boucle de metering ────────────────────────────────────────────────────

  useEffect(() => {
    const loop = () => {
      const nextLevels = new Map<string, SourceLevels>();
      const lufsMode   = getVuMeterMode() === 'lufs';
      const now        = performance.now();

      connectedSourcesRef.current.forEach(sourceId => {
        const analysers = getSourceAnalysers(sourceId);
        if (!analysers) return;

        const audioCfg = getAudioCfgRef.current?.(sourceId);
        const isMuted  = audioCfg?.muted ?? false;

        let leftPct = 0, rightPct = 0;

        // Mode LUFS : loudness Momentary (400 ms, pondération K) de la source, la même
        // valeur sur L et R — la loudness se somme sur les canaux (BS.1770). Sans
        // branche pondérée (navigateur sans IIRFilterNode), on retombe sur la crête.
        const loud = lufsMode && !isMuted ? getSourceLoudnessAnalysers(sourceId) : null;
        if (loud) {
          const last = loudLastRef.current.get(sourceId);
          let lufs = last?.lufs ?? LUFS_FLOOR;
          if (!last || now - last.at >= LUFS_REFRESH_MS) {
            let buf = loudBuffersRef.current.get(sourceId);
            if (!buf || buf.dataL.length !== loud.analyserL.fftSize) {
              buf = { dataL: new Float32Array(loud.analyserL.fftSize), dataR: new Float32Array(loud.analyserR.fftSize) };
              loudBuffersRef.current.set(sourceId, buf);
            }
            loud.analyserL.getFloatTimeDomainData(buf.dataL);
            loud.analyserR.getFloatTimeDomainData(buf.dataR);
            const n = Math.round(MOMENTARY_WINDOW_S * loud.analyserL.context.sampleRate);
            lufs = lufsFromMeanSquares([meanSquareTail(buf.dataL, n), meanSquareTail(buf.dataR, n)]);
            loudLastRef.current.set(sourceId, { at: now, lufs });
          }
          const pct = dbToPct(lufs);
          nextLevels.set(sourceId, { avg: pct, left: pct, right: pct });
          return;
        }

        if (!isMuted) {
          // Buffers Float32 — taille = fftSize de l'analyseur
          let buffers = floatBuffersRef.current.get(sourceId);
          if (!buffers || buffers.dataL.length !== analysers.analyserL.fftSize) {
            buffers = {
              dataL: new Float32Array(analysers.analyserL.fftSize),
              dataR: new Float32Array(analysers.analyserR.fftSize),
            };
            floatBuffersRef.current.set(sourceId, buffers);
          }

          analysers.analyserL.getFloatTimeDomainData(buffers.dataL);
          analysers.analyserR.getFloatTimeDomainData(buffers.dataR);

          // Le volume de la source est DÉJÀ dans le signal mesuré : applyStoredLevels le
          // pose sur le gainNode du registry, en amont des analyseurs. Le remultiplier ici
          // le comptait deux fois (fader à −6 dB → VU à −12, avant le 2026-10-23).
          const gain = 1;

          let meter = meterInstancesRef.current.get(sourceId);
          if (!meter) {
            meter = createMeter('ppm');
            meterInstancesRef.current.set(sourceId, meter);
          }

          const levels = meter.measure(buffers.dataL, buffers.dataR, gain);
          leftPct  = dbToPct(levels.left);
          rightPct = dbToPct(levels.right);
        }

        nextLevels.set(sourceId, {
          avg  : (leftPct + rightPct) / 2,
          left : leftPct,
          right: rightPct,
        });
      });

      // v5.0 — publication hors React. Plus aucun setState ici : les VU-mètres
      // s'abonnent au store et écrivent le DOM directement. La bande morte de 1 %
      // qui filtrait les rendus n'a plus lieu d'être — on publie chaque frame,
      // ce qui rend d'ailleurs les mètres plus fluides qu'avant.
      audioLevelStore.publish(nextLevels);

      meterRafRef.current = requestAnimationFrame(loop);
    };

    meterRafRef.current = requestAnimationFrame(loop);
    return () => {
      if (meterRafRef.current !== null) {
        cancelAnimationFrame(meterRafRef.current);
        meterRafRef.current = null;
      }
    };
  }, [getSourceAnalysers, getSourceLoudnessAnalysers]);

  // ── Cleanup ───────────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      connectedSourcesRef.current.forEach(id => { disconnectAudioForSource(id); });
      connectedSourcesRef.current.clear();
      sourceHoldsRef.current.forEach(hold => { try { hold.release(); } catch {} });
      sourceHoldsRef.current.clear();
      meterInstancesRef.current.clear();
      floatBuffersRef.current.clear();
      // Remet les VU-mètres encore montés à zéro plutôt que de les laisser figés
      // sur la dernière frame mesurée.
      audioLevelStore.reset();
      if (meterRafRef.current !== null) {
        cancelAnimationFrame(meterRafRef.current);
        meterRafRef.current = null;
      }
    };
  }, [disconnectAudioForSource]);

  return {
    connectAudioForSlot,
    disconnectAudioForSlot,
    changeAudioDevice,
  };
}